export interface TrackingImportRow {
  orderId: string;
  countryCode: string;
  trackingNumber: string;
  status: string;
}

export interface TrackingImportResult {
  data: TrackingImportRow[];
  errors: string[];
}

const EXPECTED_HEADERS = ['Order ID', 'Country Code', 'Tracking Number', 'Status'];

const VALID_STATUSES = ['shipped', 'processing', 'completed', 'cancelled', 'on-hold', 'refunded', 'prepare-shipment'];

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current);
  return result.map((field) => field.replace(/^"|"$/g, '').trim());
}

function validateRow(row: TrackingImportRow, index: number): string[] {
  const errors: string[] = [];
  const rowNum = index + 2; // +2 because: 1-indexed + header row

  if (!row.orderId) {
    errors.push(`Row ${rowNum}: Order ID is required`);
  } else if (!/^\d+$/.test(row.orderId)) {
    errors.push(`Row ${rowNum}: Order ID must be numeric (got "${row.orderId}")`);
  }

  if (!row.trackingNumber) {
    errors.push(`Row ${rowNum}: Tracking Number is required`);
  }

  if (row.status && !VALID_STATUSES.includes(row.status.toLowerCase())) {
    errors.push(`Row ${rowNum}: Invalid status "${row.status}" (allowed: ${VALID_STATUSES.join(', ')})`);
  }

  return errors;
}

function validateHeaders(headerLine: string): string | null {
  const headers = parseCSVLine(headerLine);
  for (let i = 0; i < EXPECTED_HEADERS.length; i++) {
    if (headers[i]?.toLowerCase() !== EXPECTED_HEADERS[i]?.toLowerCase()) {
      return `Invalid CSV header. Expected "${EXPECTED_HEADERS.join(',')}" but got "${headers.join(',')}"`;
    }
  }
  return null;
}

export function parseTrackingCSV(csvText: string): TrackingImportResult {
  const lines = csvText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line);

  const data: TrackingImportRow[] = [];
  const errors: string[] = [];

  if (lines.length === 0) {
    errors.push('CSV file is empty');
    return { data, errors };
  }

  if (lines.length === 1) {
    errors.push('CSV file has no data rows (only header)');
    return { data, errors };
  }

  const headerError = validateHeaders(lines[0]!);
  if (headerError) {
    errors.push(headerError);
    return { data, errors };
  }

  const dataLines = lines.slice(1);

  dataLines.forEach((line, index) => {
    const fields = parseCSVLine(line);

    if (fields.length < 4) {
      errors.push(`Row ${index + 2}: Expected 4 columns but got ${fields.length}`);
      return;
    }

    const row: TrackingImportRow = {
      orderId: fields[0] || '',
      countryCode: fields[1] || '',
      trackingNumber: fields[2] || '',
      status: fields[3] || '',
    };

    const rowErrors = validateRow(row, index);
    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
    } else {
      data.push(row);
    }
  });

  return { data, errors };
}
